import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  output,
  signal
} from '@angular/core';
import {
  NzDropDownDirective,
  NzDropdownMenuComponent
} from 'ng-zorro-antd/dropdown';
import { NzAvatarComponent, NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzMenuDirective, NzMenuItemComponent } from 'ng-zorro-antd/menu';
import { FormsModule } from '@angular/forms';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { SHARED_ZORRO_MODULES } from '@shared';
import { Router } from '@angular/router';
import { CdkCopyToClipboard } from '@angular/cdk/clipboard';
import { _HttpClient } from '@delon/theme';
import { finalize, Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AppService } from '../../../../shared/services/app.service';
import { AuthService } from '../../../../core/util/auth.service';
import { CreateCompanyComponent } from '../../../../routes/account/my-companies/create-company/create-company.component';

interface MyCompaniesLightweightItem {
  tenantId: string;
  companyName: string;
  tenantCode: string;
  pictureUrl?: string;
  isSample?: boolean;
  isPin?: boolean;
  isSupport?: boolean;
  supportAccess?: boolean;
  subscriptionStatus?: string;
}

interface MyCompaniesLightweightPage {
  count: number;
  value: MyCompaniesLightweightItem[];
}

const MY_COMPANIES_LIGHTWEIGHT_PAGE_SIZE = 20;

@Component({
  selector: 'app-header-company-widget',
  standalone: true,
  imports: [NzAvatarModule, 
    NzDropDownDirective,
    NzAvatarComponent,
    NzDropdownMenuComponent,
    NzInputGroupComponent,
    NzMenuItemComponent,
    FormsModule,
    NzIconDirective,
    ...SHARED_ZORRO_MODULES,
    NzMenuDirective,
    CdkCopyToClipboard,
    CreateCompanyComponent
  ],
  templateUrl: './header-company-widget.component.html',
  styleUrl: `./header-company-widget.component.less`,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderCompanyWidgetComponent implements OnDestroy {
  company = input<any>();
  collapsed = input<boolean>(false);
  companies = signal<MyCompaniesLightweightItem[]>([]);
  loading = signal(false);
  loadingMore = signal(false);
  totalCount = signal(0);
  dropdownOpen = signal(false);
  readonly #router = inject(Router);
  readonly #http = inject(_HttpClient);
  readonly #auth = inject(AuthService);

  readonly #modalSrv = inject(NzModalService);
  readonly #appSrv = inject(AppService);
  searchCompanyText = signal('');
  readonly #searchChanges$ = new Subject<string>();
  readonly #destroy$ = new Subject<void>();
  #loadedOnce = false;

  showLicenseInfo = output<void>();

  hasMore = computed(() => this.companies().length < this.totalCount());

  isDemo = computed(() => this.company()?.isSample);

  companyInitials = computed(() => {
    return this.getCompanyInitials(this.company()?.companyName);
  });

  companyIsLicensed = computed(
    () =>
      this.company()?.licensed && !this.isDemo() && !this.company()?.isFreeTrial
  );

  constructor() {
    this.#searchChanges$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.#destroy$))
      .subscribe(search => {
        if (this.dropdownOpen()) {
          this.loadCompanies(search, false);
        }
      });
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  onCompanyIdClicked = () => {
    if (this.companyIsLicensed()) {
      this.showLicenseInfo.emit();
    }
  };

  onDropdownVisibleChange(visible: boolean) {
    this.dropdownOpen.set(visible);

    if (visible && !this.#loadedOnce) {
      this.loadCompanies(this.searchCompanyText(), false);
    }
  }

  onSearchChange(value: string) {
    this.searchCompanyText.set(value);
    this.#searchChanges$.next(value.trim());
  }

  onCompanyListScroll(event: Event) {
    const target = event.target as HTMLElement;
    if (!target || this.loading() || this.loadingMore() || !this.hasMore()) {
      return;
    }

    const search = this.searchCompanyText().trim();
    if (!search) {
      return;
    }

    const nearBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

    if (nearBottom) {
      this.loadCompanies(search, true);
    }
  }

  loadCompanies(search: string, append: boolean) {
    const trimmedSearch = search.trim();
    const skip = append ? this.companies().length : 0;

    if (append) {
      this.loadingMore.set(true);
    } else {
      this.loading.set(true);
    }

    const params: Record<string, string | number> = {
      skip,
      take: MY_COMPANIES_LIGHTWEIGHT_PAGE_SIZE
    };
    if (trimmedSearch) {
      params['q'] = trimmedSearch;
    }

    this.#http
      .get<MyCompaniesLightweightPage>(
        'api/companies/MyCompanies/Lightweight',
        params
      )
      .pipe(
        finalize(() => {
          this.loading.set(false);
          this.loadingMore.set(false);
          this.#loadedOnce = true;
        })
      )
      .subscribe(res => {
        const rows = res?.value ?? [];
        this.totalCount.set(res?.count ?? rows.length);
        this.companies.set(append ? [...this.companies(), ...rows] : rows);
      });
  }

  switchCompany(company: MyCompaniesLightweightItem) {
    const tid = company.tenantId;

    if (tid === this.company().tenantId) return;

    this.#modalSrv.confirm({
      nzTitle: 'Switch Company?',
      nzContent: `Switch to <strong>[${company.companyName}]</strong>
        <div>Please make sure you have saved all of your changes.</div>
        `,
      nzOkText: 'Open Company',
      nzOnOk: () => {
        this.#auth.switchCompany(tid).then(
          () => {
            this.#appSrv.forceReloadAndReturnHome();
          },
          () => {}
        );
      }
    });
  }

  getInitials(companyName: string): string {
    return this.getCompanyInitials(companyName);
  }

  getCompanyInitials(companyName: string): string {
    if (!companyName) return '?';

    const words = companyName.trim().split(/\s+/);

    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    } else {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
  }

  goToMyCompanies(tab: 'active' | 'sample' = 'active') {
    this.#router.navigate(['/account/companies/' + tab]);
  }
}
